import { Box, Text, Textarea, Button, Loader, Paper, Stack, Select } from '@mantine/core';
import { apiInstance } from '../../services/api_service';
import { useEffect, useState, useCallback } from 'react';

const LogsTable = ({ row, context, onLogUpdate }) => {
  const studentId = row.original.id;
  const [value, setValue] = useState('');
  const [source, setSource] = useState('');
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsChanged, setLogsChanged] = useState(0);

  // Centralized source translation mapping
  const sourceTranslations = {
    'email': 'Email',
    'phonecall': 'Ligação telefônica',
    'in_person': 'Presencial',
    'meeting': 'Reunião',
    'tellme': "Tellme",
    'other': 'Outro'
  };

  const submitComment = async () => {
    // Validation: check if both comment and source are provided
    if (!value.trim() || !source) {
      return;
    }
    
    setIsLoading(true);
    let payload = {
      "reason": value,
      "context": context,
      "source": source,
    }
    try {
      const response = await apiInstance.post(`/students/${studentId}/log/`, payload);
      await new Promise(resolve => setTimeout(resolve, 1500));
      setLogsChanged(logsChanged + 1);
      setValue('');
      setSource('');
      
      // Update the parent component with the new log creator's name
      if (response.data && response.data.created_by_user) {
        const userName = `${response.data.created_by_user.first_name} ${response.data.created_by_user.last_name}`;
        onLogUpdate(studentId, userName);
      }
    } catch (error) {
      console.error('Error making request:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLogs = useCallback(async () => {
    setIsLoadingLogs(true);
    try {
      const response = await apiInstance.get(`/students/${studentId}/log/?context=${context}`);
      setLogs(response.data.results);
      setIsLoadingLogs(false);
    } catch (error) {
      console.error(error);
      setIsLoadingLogs(false);
    }
  }, [studentId, context]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, logsChanged]);

  return (
    <Box 
      sx={{ 
        width: '100%', 
      }}
    >
      {/* Comment Input - Responsive */}
      <Box 
        sx={{
          marginBottom: '12px',
        }}
      >
        {/* Source Select */}
        <Box sx={{ marginBottom: '8px' }}>
          <Select
            value={source}
            onChange={setSource}
            placeholder="Selecione a origem do comentário"
            data={[
              { value: 'email', label: sourceTranslations.email },
              { value: 'phonecall', label: sourceTranslations.phonecall },
              { value: 'in_person', label: sourceTranslations.in_person },
              { value: 'meeting', label: sourceTranslations.meeting },
              { value: 'tellme', label: sourceTranslations.tellme },
              { value: 'other', label: sourceTranslations.other }
            ]}
            required
            sx={{
              '@media (min-width: 769px)': {
                maxWidth: '280px',
              }
            }}
          />
        </Box>

        {/* Comment Input Row */}
        <Box 
          sx={{
            '@media (min-width: 769px)': {
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
            }
          }}
        >
          <Textarea
            value={value}
            onChange={(event) => setValue(event.currentTarget.value)}
            placeholder="Digite aqui um novo comentário"
            radius="md"
            minRows={3}
            sx={{
              flex: 1,
              '@media (max-width: 768px)': {
                marginBottom: '8px',
              }
            }}
          />
          <Button 
            onClick={submitComment} 
            loading={isLoading}
            disabled={!value.trim() || !source}
            sx={{
              '@media (max-width: 768px)': {
                width: '100%',
              }
            }}
          >
            Enviar
          </Button>
        </Box>
      </Box>

      {/* Logs List */}
      <Box>
        {isLoadingLogs ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '12px' }}>
            <Loader size="sm" />
          </Box>
        ) : logs.length > 0 ? (
          <Paper 
            withBorder 
            p="xs" 
            radius="md"
          >
            {/* Logs List */}
            <Box>
              <Stack spacing="md">
                {logs.map((log, index) => (
                  <Paper 
                    key={index}
                    p="md" 
                    radius="md"
                    withBorder
                    sx={{
                      backgroundColor: '#fafbfc',
                      border: '1px solid #e9ecef',
                    }}
                  >
                    <Stack spacing="sm">
                      {/* Comment Content */}
                      <Text size="md" sx={{ lineHeight: 1.5 }}>
                        {log.reason}
                      </Text>
                      
                      {/* Metadata */}
                      <Box 
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingTop: '8px',
                          borderTop: '1px solid #e9ecef',
                          '@media (max-width: 768px)': {
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '8px',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Text size="sm" color="dimmed" weight={500}>
                            👤 {log.created_by_user.first_name} {log.created_by_user.last_name}
                          </Text>
                          {log.source && (
                            <Text size="sm" color="blue" weight={500}>
                              📞 {sourceTranslations[log.source] || log.source}
                            </Text>
                          )}
                        </Box>
                        <Text size="sm" color="dimmed">
                          🕒 {new Date(log.created_at).toLocaleString('pt-BR')}
                        </Text>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Paper>
        ) : (
          <Box 
            sx={{ 
              padding: '12px',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '1px dashed #dee2e6',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa',
            }}
          >
            <Text size="sm" color="gray" style={{ fontStyle: 'italic' }}>
              Nenhum comentário encontrado
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LogsTable;
